import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Layout from '../../components/Layout';
import productBundleService, { UpdateProductBundleDTO } from '../../services/productBundleService';
import { productService } from '../../services/productService';
import { Product } from '../../types';
import { getServerErrorMessage } from '../../utils/errorUtils';
import '../../styles/ImsTheme.css';
import './ProductBundleFormPage.css';
import { ArrowLeft, Save, Plus, Trash2 } from 'lucide-react';

const MAX_LENGTHS = {
  code: 40,
  name: 100,
  description: 300,
  notes: 200,
};

const NUMERIC_FIELDS = ['price', 'sortOrder'];

interface BundleLine {
  productId: number;
  quantity: number | '';
  notes: string;
}

interface BundleForm {
  code: string;
  name: string;
  description: string;
  price: number | '';
  sortOrder: number | '';
  isActive: boolean;
}

interface FieldErrors {
  code?: string;
  name?: string;
  description?: string;
  price?: string;
  sortOrder?: string;
  items?: string;
}

const emptyLine = (): BundleLine => ({ productId: 0, quantity: 1, notes: '' });

const ProductBundleEditPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const [saving, setSaving] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [products, setProducts] = useState<Product[]>([]);
  const [items, setItems] = useState<BundleLine[]>([]);
  const [formData, setFormData] = useState<BundleForm>({
    code: '',
    name: '',
    description: '',
    price: '',
    sortOrder: 0,
    isActive: true,
  });

  const loadProducts = useCallback(async () => {
    try {
      const response = await productService.getProducts({ pageNumber: 1, pageSize: 500 });
      setProducts(response.data.data ?? []);
    } catch (err) {
      setError(getServerErrorMessage(err) || t('productBundle.failedToLoadProducts'));
    }
  }, [t]);

  const loadBundle = useCallback(async () => {
    if (!id) return;
    setLoadingData(true);
    setError(null);
    try {
      const bundle = await productBundleService.getById(Number(id));
      if (!bundle) {
        setError(t('productBundle.notFound'));
        return;
      }
      setFormData({
        code: bundle.code,
        name: bundle.name,
        description: bundle.description ?? '',
        price: bundle.price,
        sortOrder: bundle.sortOrder,
        isActive: bundle.isActive,
      });
      setItems(
        (bundle.items ?? []).map((item) => ({
          productId: item.productId,
          quantity: item.quantity,
          notes: item.notes ?? '',
        }))
      );
    } catch (err) {
      setError(getServerErrorMessage(err) || t('productBundle.failedToLoad'));
    } finally {
      setLoadingData(false);
    }
  }, [id, t]);

  useEffect(() => {
    loadProducts();
  }, [loadProducts]);

  useEffect(() => {
    loadBundle();
  }, [loadBundle]);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value, type } = e.target;
    const isCheckbox = type === 'checkbox';

    let finalValue: any;
    if (isCheckbox) {
      finalValue = (e.target as HTMLInputElement).checked;
    } else if (NUMERIC_FIELDS.includes(name)) {
      finalValue = value === '' ? '' : Number(value);
    } else {
      finalValue = value;
      if (name in MAX_LENGTHS && value.length > MAX_LENGTHS[name as keyof typeof MAX_LENGTHS]) {
        finalValue = value.substring(0, MAX_LENGTHS[name as keyof typeof MAX_LENGTHS]);
      }
    }

    setFormData((prev) => ({ ...prev, [name]: finalValue }));

    if (!isCheckbox) {
      setFieldErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  const handleLineChange = (index: number, field: keyof BundleLine, value: string | number) => {
    setItems((prev) =>
      prev.map((line, lineIndex) => {
        if (lineIndex !== index) return line;
        if (field === 'notes') {
          return { ...line, notes: String(value).substring(0, MAX_LENGTHS.notes) };
        }
        if (field === 'quantity') {
          return { ...line, quantity: value === '' ? '' : Number(value) };
        }
        return { ...line, productId: Number(value) };
      })
    );
    setFieldErrors((prev) => ({ ...prev, items: undefined }));
  };

  const addLine = () => setItems((prev) => [...prev, emptyLine()]);

  const removeLine = (index: number) =>
    setItems((prev) => prev.filter((_, lineIndex) => lineIndex !== index));

  const validateForm = (): boolean => {
    const errors: FieldErrors = {};
    let isValid = true;

    if (!formData.code.trim()) {
      errors.code = t('productBundle.formValidation.codeRequired');
      isValid = false;
    } else if (formData.code.length > MAX_LENGTHS.code) {
      errors.code = t('productBundle.formValidation.codeMaxLength', { max: MAX_LENGTHS.code });
      isValid = false;
    }

    if (!formData.name.trim()) {
      errors.name = t('productBundle.formValidation.nameRequired');
      isValid = false;
    } else if (formData.name.length > MAX_LENGTHS.name) {
      errors.name = t('productBundle.formValidation.nameMaxLength', { max: MAX_LENGTHS.name });
      isValid = false;
    }

    if (formData.description && formData.description.length > MAX_LENGTHS.description) {
      errors.description = t('productBundle.formValidation.descriptionMaxLength', { max: MAX_LENGTHS.description });
      isValid = false;
    }

    if (formData.price === '' || formData.price === null) {
      errors.price = t('productBundle.formValidation.priceRequired');
      isValid = false;
    } else if (Number(formData.price) <= 0) {
      errors.price = t('productBundle.formValidation.pricePositive');
      isValid = false;
    }

    if (formData.sortOrder !== '' && Number(formData.sortOrder) < 0) {
      errors.sortOrder = t('productBundle.formValidation.sortOrderNegative');
      isValid = false;
    }

    if (items.length === 0) {
      errors.items = t('productBundle.formValidation.itemsRequired');
      isValid = false;
    } else if (items.some((line) => line.productId <= 0)) {
      errors.items = t('productBundle.formValidation.itemProductRequired');
      isValid = false;
    } else if (items.some((line) => line.quantity === '' || Number(line.quantity) <= 0)) {
      errors.items = t('productBundle.formValidation.itemQuantityPositive');
      isValid = false;
    } else if (new Set(items.map((line) => line.productId)).size !== items.length) {
      errors.items = t('productBundle.formValidation.itemDuplicateProduct');
      isValid = false;
    }

    setFieldErrors(errors);

    if (!isValid) {
      const firstError = Object.values(errors).find((e) => e);
      if (firstError) setError(firstError);
    }

    return isValid;
  };

  const mapFormToDTO = (): UpdateProductBundleDTO => ({
    code: formData.code.trim().toUpperCase(),
    name: formData.name.trim(),
    description: formData.description || undefined,
    price: Number(formData.price),
    sortOrder: formData.sortOrder === '' ? 0 : Number(formData.sortOrder),
    isActive: formData.isActive,
    items: items.map((line) => ({
      productId: line.productId,
      quantity: Number(line.quantity),
      notes: line.notes || undefined,
    })),
  });

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);

    if (!validateForm()) return;

    setSaving(true);
    try {
      await productBundleService.update(Number(id), mapFormToDTO());
      navigate('/sales/product-bundles', { state: { message: t('productBundle.updateSuccess') } });
    } catch (err) {
      setError(getServerErrorMessage(err) || t('productBundle.failedToUpdate'));
      setSaving(false);
    }
  };

  if (loadingData) {
    return (
      <Layout>
        <div className="edit-container product-bundle-form-page">
          <div className="ims-loading">{t('common.loading')}</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="edit-container product-bundle-form-page">
        <div className="ims-top-bar">
          <div className="ims-top-bar-left">
            <button type="button" className="ims-back-btn" onClick={() => navigate('/sales/product-bundles')}>
              <ArrowLeft size={16} strokeWidth={2} />
            </button>
            <div className="ims-page-title">{t('productBundle.editBundle')}</div>
          </div>
          <div className="ims-top-bar-right">
            <button
              type="button"
              className="ims-btn ims-btn-secondary"
              onClick={() => navigate('/sales/product-bundles')}
              disabled={saving}
            >
              {t('common.cancel')}
            </button>
            <button type="submit" form="page-form" className="ims-btn ims-btn-primary" disabled={saving}>
              {saving
                ? <><span className="ims-btn-spinner" />{t('common.saving')}</>
                : <><Save size={15} strokeWidth={2} />{t('common.save')}</>}
            </button>
          </div>
        </div>

        {error && (
          <div className="alert alert-danger alert-dismissible fade show">
            <strong>{t('common.error')}:</strong> {error}
            <button
              type="button"
              className="btn-close"
              onClick={() => setError(null)}
              aria-label={t('common.close')}
            ></button>
          </div>
        )}

        <div className="edit-content">
          <div className="edit-form-section">
            <div className="edit-card">
              <div className="edit-card-header">
                <h2 className="edit-card-title">{t('productBundle.editBundle')}</h2>
              </div>
              <div className="edit-card-body">
                <form id="page-form" onSubmit={handleSubmit} noValidate>
                  <div className="bundle-form-row">
                    <div className="form-group">
                      <label className="form-label">
                        {t('productBundle.code')} <span className="required">*</span>
                        <span className="char-counter">{formData.code.length}/{MAX_LENGTHS.code}</span>
                      </label>
                      <input
                        type="text"
                        className={`form-input bundle-code-input ${fieldErrors.code ? 'is-invalid' : ''}`}
                        name="code"
                        value={formData.code}
                        onChange={handleInputChange}
                        maxLength={MAX_LENGTHS.code}
                        placeholder="COMBO1"
                      />
                      {fieldErrors.code && <small className="form-error">{fieldErrors.code}</small>}
                      {!fieldErrors.code && <small className="form-helper">{t('productBundle.codeHint')}</small>}
                    </div>

                    <div className="form-group">
                      <label className="form-label">
                        {t('productBundle.name')} <span className="required">*</span>
                        <span className="char-counter">{formData.name.length}/{MAX_LENGTHS.name}</span>
                      </label>
                      <input
                        type="text"
                        className={`form-input ${fieldErrors.name ? 'is-invalid' : ''}`}
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        maxLength={MAX_LENGTHS.name}
                        placeholder={t('productBundle.namePlaceholder')}
                      />
                      {fieldErrors.name && <small className="form-error">{fieldErrors.name}</small>}
                      {!fieldErrors.name && <small className="form-helper">{t('productBundle.nameHint')}</small>}
                    </div>
                  </div>

                  <div className="form-group">
                    <label className="form-label">
                      {t('productBundle.description')}
                      <span className="char-counter">{formData.description.length}/{MAX_LENGTHS.description}</span>
                    </label>
                    <textarea
                      className={`form-textarea ${fieldErrors.description ? 'is-invalid' : ''}`}
                      name="description"
                      value={formData.description}
                      onChange={handleInputChange}
                      maxLength={MAX_LENGTHS.description}
                      placeholder={t('productBundle.description')}
                      rows={3}
                    />
                    {fieldErrors.description && <small className="form-error">{fieldErrors.description}</small>}
                    {!fieldErrors.description && <small className="form-helper">{t('productBundle.descriptionHint')}</small>}
                  </div>

                  <div className="bundle-form-row">
                    <div className="form-group">
                      <label className="form-label">
                        {t('productBundle.price')} <span className="required">*</span>
                      </label>
                      <input
                        type="number"
                        className={`form-input ${fieldErrors.price ? 'is-invalid' : ''}`}
                        name="price"
                        value={formData.price}
                        onChange={handleInputChange}
                        placeholder="0"
                        step="0.01"
                        min="0"
                      />
                      {fieldErrors.price && <small className="form-error">{fieldErrors.price}</small>}
                      {!fieldErrors.price && <small className="form-helper">{t('productBundle.priceHint')}</small>}
                    </div>

                    <div className="form-group">
                      <label className="form-label">{t('productBundle.sortOrder')}</label>
                      <input
                        type="number"
                        className={`form-input ${fieldErrors.sortOrder ? 'is-invalid' : ''}`}
                        name="sortOrder"
                        value={formData.sortOrder}
                        onChange={handleInputChange}
                        step="1"
                        min="0"
                      />
                      {fieldErrors.sortOrder && <small className="form-error">{fieldErrors.sortOrder}</small>}
                      {!fieldErrors.sortOrder && <small className="form-helper">{t('productBundle.sortOrderHint')}</small>}
                    </div>
                  </div>

                  <div className="form-group">
                    <label className="form-checkbox-label">
                      <input
                        type="checkbox"
                        name="isActive"
                        checked={formData.isActive}
                        onChange={handleInputChange}
                      />
                      <span>{t('productBundle.isActive')}</span>
                    </label>
                    <small className="form-helper">{t('productBundle.isActiveHint')}</small>
                  </div>
                </form>
              </div>
            </div>

            <div className={`edit-card bundle-items-card ${fieldErrors.items ? 'ims-card--invalid' : ''}`}>
              <div className="edit-card-header">
                <h2 className="edit-card-title">{t('productBundle.bundleItems')}</h2>
                <button type="button" className="ims-btn ims-btn-secondary bundle-add-btn" onClick={addLine}>
                  <Plus size={13} strokeWidth={2.5} />
                  {t('productBundle.addItem')}
                </button>
              </div>
              <div className="edit-card-body">
                {fieldErrors.items && <small className="form-error">{fieldErrors.items}</small>}

                {items.length === 0 ? (
                  <div className="ims-items-empty">{t('productBundle.addItemsHint')}</div>
                ) : (
                  <div className="ims-table-scroll">
                    <table className="ims-table">
                      <thead>
                        <tr>
                          <th className="ims-th" style={{ minWidth: 220 }}>{t('productBundle.product')}</th>
                          <th className="ims-th ims-th--num" style={{ minWidth: 110 }}>{t('productBundle.quantity')}</th>
                          <th className="ims-th" style={{ minWidth: 180 }}>{t('productBundle.notes')}</th>
                          <th className="ims-th ims-th--del" />
                        </tr>
                      </thead>
                      <tbody>
                        {items.map((line, index) => (
                          <tr key={index} className="ims-tr">
                            <td className="ims-td">
                              <select
                                className="ims-cell-input"
                                value={line.productId}
                                onChange={(e) => handleLineChange(index, 'productId', e.target.value)}
                              >
                                <option value={0}>{t('productBundle.selectProduct')}</option>
                                {products.map((product) => (
                                  <option key={product.id} value={product.id}>
                                    {product.name} ({product.sku})
                                  </option>
                                ))}
                              </select>
                            </td>
                            <td className="ims-td ims-td--num">
                              <input
                                type="number"
                                className="ims-cell-input ims-num-input"
                                step="0.01"
                                min={0.01}
                                value={line.quantity}
                                onChange={(e) => handleLineChange(index, 'quantity', e.target.value)}
                                placeholder="0"
                              />
                            </td>
                            <td className="ims-td">
                              <input
                                type="text"
                                className="ims-cell-input"
                                value={line.notes}
                                maxLength={MAX_LENGTHS.notes}
                                onChange={(e) => handleLineChange(index, 'notes', e.target.value)}
                                placeholder={t('productBundle.notesPlaceholder')}
                              />
                            </td>
                            <td className="ims-td ims-td--del">
                              <button
                                type="button"
                                className="bundle-remove-btn"
                                onClick={() => removeLine(index)}
                                title={t('common.delete')}
                                aria-label={t('common.delete')}
                              >
                                <Trash2 size={15} strokeWidth={1.75} />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="edit-info-section">
            <div className="edit-card">
              <div className="edit-card-header">
                <h3 className="edit-card-title">{t('common.information')}</h3>
              </div>
              <div className="edit-card-body">
                <div className="help-item">
                  <strong>{t('productBundle.code')}</strong>
                  <p>{t('productBundle.help.code')}</p>
                </div>
                <div className="help-item">
                  <strong>{t('productBundle.price')}</strong>
                  <p>{t('productBundle.help.price')}</p>
                </div>
                <div className="help-item">
                  <strong>{t('productBundle.bundleItems')}</strong>
                  <p>{t('productBundle.help.items')}</p>
                </div>
                <div className="help-item">
                  <strong>{t('productBundle.sortOrder')}</strong>
                  <p>{t('productBundle.help.sortOrder')}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default ProductBundleEditPage;
